import { CloseSquare, DocumentUpload, Edit } from "iconsax-react";
import { useEffect, useState } from "react";
import { useLoaderData } from "react-router-dom";
import { OrderSummaryModal } from "../../../components/modals/order/OrderSummary";
import NormalSelectInputField from "../../../components/inputs/NormalSelectInputField";
import { Autocomplete, TextField } from "@mui/material";
import NormalInputField from "../../../components/inputs/NormalInputField";
import instance from "../../../axios/instance";
import { getToken } from "../../../utils/token";
import { generateRandomId } from "../../../utils/data";
import { toast } from "react-toastify";
import PrimaryAddButton from "../../../components/buttons/PrimaryAddButton";

export const editOrderLoader = async ({ params }) => {
  const token = getToken();

  const order = await instance.post(
    "/doctor/order/single",
    { orderId: params.orderId },
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (!order.data)
    return {
      order: null,
      drugs: [],
      facility: {},
      doctors: [],
    };

  const drugs = await instance.post(
    "/doctor/pharmacy/drugs",
    { pharmacyId: order.data.pharmacy_id },
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );

  const facility = await instance.get("/doctor/facility", {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  const doctors = await instance.get("/doctor/facility/doctors", {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  const refillRequest = await instance.get(
    `/doctor/refill/single/${params.id}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );

  return {
    drugs: drugs.data,
    facility: facility.data,
    doctors: doctors.data.map((_) => ({
      value: _.id,
      label: `${_.lastname} ${_.firstname} - ${_.id}`,
    })),
    order: order.data,
    refillRequest: refillRequest.data,
  };
};

export default function EditOrderPage(props) {
  const token = getToken();
  const { drugs, facility, doctors, order, refillRequest } = useLoaderData();

  if (!order) {
    toast.error("This order does not exist");
    setTimeout(() => {
      history.back();
    }, 100);
    return;
  }

  const pharmacyDrugs = drugs.map((drug) => ({
    ...drug,
    id: drug.id,
    label: `${drug.name} - ${drug.mg} - ${drug.size}`,
  }));
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [orderType, setOrderType] = useState("Refill");
  const [orderBillingPreference, setOrderBillingPreference] = useState(
    order.order_billing_preference || "Bill Patient"
  );
  const [deliveryType, setDeliveryType] = useState(order.delivery_type || "");
  const [deliveryAddress, setDeliveryAddress] = useState(
    order.delivery_address || ""
  );
  const [deliveryPhone, setDeliveryPhone] = useState(
    order.delivery_phone || ""
  );
  const medication = order.medication ? JSON.parse(order.medication) : [];
  const [numberOfRows, setNumberOfRows] = useState(medication.length || 1);
  const [medications, setMedications] = useState(medication || []);
  const [orderMethod, setOrderMethod] = useState("Patient");
  const [orderNote, setOrderNote] = useState(order.order_note || "");
  const [file, setFile] = useState(order.file || null);
  const [isAddPatientModalOpen, setIsAddPatientModalOpen] = useState(false);
  // const [doctors, setDoctors] = useState([]);
  const [priority, setPriority] = useState(order.priority || "regular");
  const [supervisingPractitionerId, setSupervisingPractitionerId] = useState(
    order.supervising_practitioner_id || ""
  );
  const [showSummary, setShowSummary] = useState(false);
  const [newOrderId, setNewOrderId] = useState("");
  const [newOrder, setNewOrder] = useState(null);

  const onFileChange = (event) => {
    setFile(event.target.files[0]);
  };

  const isButtonNotActive =
    !orderType ||
    !deliveryType ||
    !deliveryAddress ||
    !deliveryPhone ||
    medications.length < 1 ||
    !orderMethod ||
    !orderBillingPreference ||
    !supervisingPractitionerId ||
    !priority;

  // update medication state
  const updateMedication = (prevMedications, index, field, value) => {
    const updatedMedications = [...prevMedications];
    const medication = updatedMedications[index] || {};
    medication[field] = value;
    updatedMedications[index] = medication;
    return updatedMedications;
  };

  const handleCreateOrder = () => {
    const formData = new FormData();
    formData.append("file", file);
    setErrorMessage("");
    setIsLoading(true);
    formData.append("patientId", order.patient.id);
    formData.append("orderType", orderType);
    formData.append("orderBillingPreference", orderBillingPreference);
    formData.append("deliveryType", deliveryType);
    formData.append("deliveryAddress", deliveryAddress);
    formData.append("deliveryPhone", deliveryPhone);
    formData.append("orderMethod", orderMethod);
    formData.append("pharmacyId", order.pharmacy.id);
    formData.append("medication", JSON.stringify(medications));
    formData.append("orderNote", orderNote);
    formData.append("supervisingPractitionerId", supervisingPractitionerId);
    formData.append("priority", priority);
    instance
      .post("/doctor/order/create", formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      })
      .then((response) => {
        // props.triggerRevalidate();
        // props.onClose();
        setNewOrderId(response.data.orderId);
        setShowSummary(true);
        setIsLoading(false);
      })
      .catch((error) => {
        console.log(error.response.data);
        setErrorMessage(error.response.data.message);
        setIsLoading(false);
      });
  };

  const generateRows = (defNum) => {
    setNumberOfRows((num) => num + 1);
    const id = generateRandomId();
    setMedications((prev) => updateMedication(prev, numberOfRows, "id", id));
  };

  const removeRows = () => {
    if (numberOfRows > 1) {
      setMedications((prev) =>
        prev.filter((val, index) => index !== numberOfRows - 1)
      );
      setNumberOfRows((num) => num - 1);
    }
  };

  useEffect(() => {
    const id = generateRandomId();
    setMedications((prev) =>
      updateMedication(prev, numberOfRows - 1, "id", id)
    );
  }, []);

  useEffect(() => {
    let loaded = true;
    if (showSummary && newOrderId) {
      (async () => {
        try {
          const _order = await instance.post(
            "/doctor/order/single",
            { orderId: newOrderId },
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );
          await instance.put(
            "/doctor/refill/request/complete",
            {
              orderId: newOrderId,
              id: refillRequest.id,
              patientId: order.patient.id,
            },
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );
          if (loaded) {
            setNewOrder(_order.data);
          }
        } catch (e) {
          props.triggerRevalidate();
          props.onClose();
        }
      })();
    }

    return () => {
      loaded = false;
    };
  }, [showSummary, newOrderId]);

  return (
    <>
      {newOrder && (
        <OrderSummaryModal
          order={newOrder}
          isRefill={false}
          onClose={() => {
            setNewOrder(false);
            history.back();
          }}
        />
      )}
      <div
        className={`fixed top-0 right-0 left-0 bottom-0 z-40 bg-white overflow-y-scroll scrollbar-hide`}
      >
        <div className="sticky left-0 right-0 top-0">
          <div className="bg-[#f1f1f1] px-5 py-4 flex justify-between">
            <div className="text-[18px] font-bold select-none">
              Create Refill Order (Rx) for {order.patient.lastname}{" "}
              {order.patient.firstname}
            </div>
            <button onClick={() => history.back()}>
              <CloseSquare variant="Bold" />
            </button>
          </div>
        </div>

        <div className="px-5 py-3 text-[14px] space-y-4 mt-4">
          {/* <label htmlFor="priority">Priority</label> */}
          <label
            htmlFor="priority"
            className="block font-semibold uppercase text-[12px] text-gray-600"
          >
            Priority
            <span className="text-red-500">*</span>
          </label>
          <input
            type="radio"
            name="priority"
            id="priority"
            onChange={(e) => setPriority("regular")}
            defaultChecked
            className="!mt-2 mr-1"
          />{" "}
          Regular - $25 2 Days
          <input
            type="radio"
            name="priority"
            id="priority"
            className="!mt-2 ml-5 mr-1"
            onChange={(e) => setPriority("urgent")}
          />{" "}
          Urgent - $40 OVN
          <div className="!mt-5 grid grid-cols-4 gap-x-5 mb-5">
            <NormalSelectInputField
              title="Order Method"
              values={[
                { label: "Bulk Order/Office use", value: "Bulk" },
                { label: "Patient Order", value: "Patient" },
              ]}
              isRequired={true}
              value={orderMethod}
              disabled={true}
            />
            <NormalSelectInputField
              title="Order Type"
              values={["New", "Refill"]}
              isRequired={true}
              value={orderType}
              onChange={(e) => setOrderType(e.target.value)}
              disabled={true}
            />
            <NormalSelectInputField
              title="Billing Preference"
              values={["Bill Facility", "Bill Patient"]}
              isRequired={true}
              value={orderBillingPreference}
              onChange={(e) => setOrderBillingPreference(e.target.value)}
            />

            <NormalSelectInputField
              title="Delivery Type"
              values={["Deliver to Patient", "Deliver to Doctor"]}
              isRequired={true}
              value={deliveryType}
              onChange={(e) => setDeliveryType(e.target.value)}
            />
          </div>
          <div className="grid grid-cols-2 gap-x-5">
            <Autocomplete
              disablePortal
              id="combo-box-demo"
              options={doctors}
              onChange={(e, val) => setSupervisingPractitionerId(val.value)}
              value={supervisingPractitionerId}
              renderInput={(params) => (
                <TextField
                  {...params}
                  required={true}
                  label="Select supervising practitioner"
                />
              )}
            />
            <NormalSelectInputField
              title="Select a Pharmacy"
              values={[
                {
                  value: order.pharmacy.id,
                  label: order.pharmacy.pharmacy_name,
                },
              ]}
              isRequired={true}
              onChange={() => {}}
              disabled={true}
              value={order.pharmacy.id}
            />
          </div>
          <div className={`grid grid-cols-[auto_35%] gap-x-5 mb-5`}>
            <NormalInputField
              title="Delivery Address"
              isRequired={true}
              value={deliveryAddress}
              onChange={(e) => setDeliveryAddress(e.target.value)}
            />
            <NormalInputField
              title="Delivery Phone"
              isRequired={true}
              value={deliveryPhone}
              onChange={(e) => setDeliveryPhone(e.target.value)}
            />
          </div>
        </div>

        <div className="px-5 py-3 mt-7">
          <div className="font-bold text-[18px] mb-4">Create Prescription</div>
          <div className="flex gap-x-2 items-center">
            Rows:
            <div className="grid grid-cols-3 max-w-[150px] gap-1">
              <button
                disabled={!numberOfRows ? true : false}
                onClick={() => removeRows()}
                className="bg-primary px-3 flex justify-center items-center shadow-sm text-[20px] rounded-[4px] text-white"
              >
                -
              </button>
              <p className="rounded-md border flex items-center justify-center px-2 py-1">
                {numberOfRows}
              </p>
              <button
                disabled={!numberOfRows ? true : false}
                onClick={() => {
                  generateRows();
                }}
                className="bg-primary px-3 flex justify-center items-center shadow-sm text-[20px] rounded-[4px] text-white"
              >
                +
              </button>
            </div>
          </div>

          <div className="mt-4 w-full overflow-x-scroll">
            <table className="w-full">
              <thead>
                <tr className="font-semibold bg-[#f1f1f1] h-12 uppercase text-[12px] text-gray-800">
                  <td className="px-3">#</td>
                  <td className="px-3 min-w-[450px]">Medication Name</td>
                  <td className="px-3 min-w-[200px] max-w-[200px]">mg/mL</td>
                  <td className="px-3 min-w-[200px] max-w-[200px]">Size</td>
                  <td className="px-3 min-w-[150px] max-w-[150px]">
                    Route of Admin
                  </td>
                  <td className="px-3 min-w-[250px]">Device</td>
                  <td className="px-3 min-w-[250px]">Form</td>
                  <td className="px-3 min-w-[250px]">Strength</td>
                  <td className="px-3 min-w-[250px]">Category</td>
                  <td className="px-3 min-w-[250px]">RX ID</td>
                  <td className="px-3 min-w-[300px]">Directions</td>
                  <td className="px-3 min-w-[150px]">Quantity</td>

                  <td className="px-3 min-w-[350px]">
                    Last Office Visit and Diagnosis Code
                  </td>
                  <td className="px-3 min-w-[200px]">Refills</td>
                  <td className="px-3 min-w-[300px]">Reason for Compounding</td>
                </tr>
              </thead>

              <tbody>
                {new Array(numberOfRows).fill(null).map((item, index) => (
                  <RxItem
                    key={index}
                    index={index + 1}
                    drugs={pharmacyDrugs}
                    refill={orderType === "Refill"}
                    isPatient={true}
                    medication={medications[index]}
                    rxId={refillRequest.rx_id}
                    onChange={(type, data) =>
                      setMedications((prev) =>
                        updateMedication(prev, index, type, data)
                      )
                    }
                  />
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-4">
            <div>
              <div className="font-bold text-[18px] mb-2">Add Order Note</div>
              <textarea
                onChange={(e) => setOrderNote(e.target.value)}
                className="w-full border rounded-[4px] p-2 outline-none resize-none"
                placeholder="Enter order note"
                rows={7}
              />
            </div>
          </div>

          <div className="mt-4">
            <div className="font-bold text-[16px] mb-2">
              Attach Clinical Document
            </div>
            <div className="flex items-center gap-x-2">
              <DocumentUpload size="22" color="#00B0AD" />{" "}
              <input
                type="file"
                placeholder="Add an attachment"
                accept=".pdf,.docx,.xlsx"
                className="text-sm opacity-80"
                onChange={onFileChange}
              />
            </div>
          </div>

          <div className="mt-4">
            <div className="text-[14px] font-semibold py-1 text-red-500">
              {errorMessage}
            </div>
            <PrimaryAddButton
              title="Create Order +"
              onClick={handleCreateOrder}
              isLoading={isLoading}
              disabled={isButtonNotActive || isLoading}
              color="white"
            />
          </div>
        </div>
      </div>
    </>
  );
}

function RxItem(props) {
  const med = props.medication;
  const medObj = {
    mg: med?.mg || "",
    size: med?.size || "",
    device: med?.device || "",
    route_of_admin: med?.route_of_admin || "",
    form: med?.form || "",
    category: med?.category || "",
    strength: med?.strength || "",
    c2: med?.c2 || "",
    drug: med?.drug || "",
    patient_amount: med?.patient_amount || "",
    doctor_amount: med?.doctor_amount || "",
    name: med?.name || "",
    drugId: med?.drugId || "",
    rxId: props.rxId || "",
    direction: med?.direction || "",
    quantity: med?.quantity || "",
    diagnosisCode: med?.diagnosisCode || "",
    refills: med?.refills || "",
    reasonForCompounding: med?.reasonForCompounding || "",
  };
  const [medication, setMedication] = useState(medObj);

  const handleSelectDrug = (value) => {
    if (value) {
      const drug = props.drugs.find((drug) => drug.id === value.id);
      setMedication(drug);
    }
  };

  useEffect(() => {
    for (let i in medication) {
      props.onChange(i, medication[i]);
    }
  }, [medication]);

  const updateMed = (key, value) => {
    setMedication((med) => ({ ...med, [key]: value }));
  };

  return (
    <tr>
      <td className="px-3 py-3">
        <div>{props.index}</div>
      </td>
      <td className="px-3 py-3">
        {props.drugs.length >= 1 ? (
          <Autocomplete
            disablePortal
            id="combo-box-demo"
            options={props.drugs}
            onChange={(e, value) => handleSelectDrug(value)}
            renderInput={(params) => (
              <TextField {...params} label="Medication Name" />
            )}
            size="small"
            value={medication.name}
          />
        ) : (
          <input
            onChange={(e) => updateMed("name", e.target.value)}
            className="border p-2 rounded-[4px] outline-none w-full focus:border-primary"
            placeholder="Medication name"
            value={medication.name}
            required
          />
        )}
      </td>
      <td className="px-3 py-3">
        <input
          onChange={(e) => {
            updateMed("mg", e.target.value);
          }}
          className="border p-2 rounded-[4px] outline-none w-full focus:border-primary"
          placeholder="mg/mL"
          required
          value={medication.mg}
        />
      </td>
      <td className="px-3 py-3">
        <input
          onChange={(e) => {
            updateMed("size", e.target.value);
          }}
          className="border p-2 rounded-[4px] outline-none w-full focus:border-primary"
          placeholder="size"
          required
          value={medication.size}
        />
      </td>
      <td className="px-3 py-3">
        <input
          onChange={(e) => {
            updateMed("route-of-admin", e.target.value);
          }}
          className="border p-2 rounded-[4px] outline-none w-full focus:border-primary"
          placeholder="Route of Admin"
          required
          value={medication.route_of_admin}
        />
      </td>
      <td className="px-3 py-3">
        <input
          onChange={(e) => {
            updateMed("device", e.target.value);
          }}
          className="border p-2 rounded-[4px] outline-none w-full focus:border-primary"
          placeholder="Device"
          required
          value={medication.device}
        />
      </td>
      <td className="px-3 py-3">
        <input
          onChange={(e) => {
            updateMed("form", e.target.value);
          }}
          className="border p-2 rounded-[4px] outline-none w-full focus:border-primary"
          placeholder="Form"
          required
          value={medication.form}
        />
      </td>
      <td className="px-3 py-3">
        <input
          onChange={(e) => {
            updateMed("strength", e.target.value);
          }}
          className="border p-2 rounded-[4px] outline-none w-full focus:border-primary"
          placeholder="Strength"
          required
          value={medication.strength}
        />
      </td>
      <td className="px-3 py-3">
        <input
          onChange={(e) => {
            updateMed("category", e.target.value);
          }}
          className="border p-2 rounded-[4px] outline-none w-full focus:border-primary"
          placeholder="Category"
          required
          value={medication.category}
        />
      </td>
      <td className="px-3 py-3">
        <input
          onChange={(e) => updateMed("rxId", e.target.value)}
          className="border p-2 rounded-[4px] outline-none w-full focus:border-primary"
          placeholder="RX ID"
          required
          value={medication.rxId}
        />
      </td>
      <td className="px-3 py-3">
        <input
          onChange={(e) => updateMed("direction", e.target.value)}
          className="border p-2 rounded-[4px] outline-none w-full focus:border-primary"
          placeholder="Directions"
          required
          value={medication.direction}
        />
      </td>
      <td className="px-3 py-3">
        <input
          onChange={(e) => updateMed("quantity", e.target.value)}
          className="border p-2 rounded-[4px] outline-none w-full focus:border-primary"
          placeholder="Quantity"
          required
          value={medication.quantity}
        />
      </td>
      <td className="px-3 py-3">
        <input
          onChange={(e) => updateMed("diagnosisCode", e.target.value)}
          className="border p-2 rounded-[4px] outline-none w-full focus:border-primary"
          placeholder="Last office visit and diagnosis code"
          required
          value={medication.diagnosisCode}
        />
      </td>

      <td className="px-3 py-3">
        <input
          onChange={(e) => updateMed("refills", e.target.value)}
          className="border p-2 rounded-[4px] outline-none w-full focus:border-primary"
          placeholder="Refills"
          required
          value={medication.refills}
        />
      </td>
      <td className="pl-3 py-3">
        <input
          onChange={(e) => updateMed("reasonForCompounding", e.target.value)}
          className="border p-2 rounded-[4px] outline-none w-full focus:border-primary"
          placeholder="Reason for compounding"
          required
          value={medication.reasonForCompounding}
        />
      </td>
    </tr>
  );
}
